import { useEffect, useRef, useState } from "react";
import PageLoader from "./PageLoader";
import { isNetworkIdle } from "../Utils/networkTracker";

// Keeps the loader on screen until every API request of the page has finished and
// the images of the first screen have loaded, then shows the whole page at once.
const IMAGE_WAIT_MS = 4000;
const MAX_WAIT_MS = 12000;
const POLL_MS = 100;

const ContentReveal = ({ children }) => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    let finished = false;
    let waitingForImages = false;
    const finish = () => {
      if (!finished) {
        finished = true;
        setRevealed(true);
      }
    };

    const waitForImages = () => {
      const root = ref.current;
      if (!root) return finish();
      const limit = window.innerHeight;
      const pending = Array.from(root.querySelectorAll("img")).filter((img) => {
        if (img.complete) return false;
        const rect = img.getBoundingClientRect();
        return rect.top < limit && rect.bottom > 0;
      });
      if (pending.length === 0) return finish();
      // lazy images do not start loading while the page is hidden, so load the first-screen ones now
      pending.forEach((img) => {
        img.loading = "eager";
      });
      setTimeout(finish, IMAGE_WAIT_MS);
      Promise.all(
        pending.map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
        )
      ).then(finish);
    };

    const poll = setInterval(() => {
      if (finished || waitingForImages) return;
      if (isNetworkIdle()) {
        waitingForImages = true;
        // give React a moment to paint the data that just arrived
        setTimeout(waitForImages, 100);
      }
    }, POLL_MS);
    const maxWait = setTimeout(finish, MAX_WAIT_MS);

    return () => {
      finished = true;
      clearInterval(poll);
      clearTimeout(maxWait);
    };
  }, []);

  return (
    <div className={revealed ? "content-reveal" : "content-reveal content-reveal-hidden"}>
      {!revealed && (
        <div className="content-reveal-loader">
          <PageLoader />
        </div>
      )}
      <div ref={ref}>{children}</div>
    </div>
  );
};

export default ContentReveal;
